import React, { useCallback, useEffect, useState } from "react";

import { faSortAmountDown } from "@fortawesome/pro-solid-svg-icons/faSortAmountDown";
import { faSortAmountUp } from "@fortawesome/pro-solid-svg-icons/faSortAmountUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import { SizeType } from "antd/es/config-provider/SizeContext";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Button, SelectField } from "@mapmycustomers/ui";

import layout from "@app/styles/layout";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import styles from "./ActivitySort.module.scss";
import getSortDirectionNameForField from "./utils/getSortDirectionNameForField";
import { sortOptionsMessages } from "./utils/sortOptionsMessages";
import { useSortOptions } from "./utils/useSortOptions";

interface Props {
  activityStatusOption: ActivityStatusOption;
  field: IField;
  onApply: () => void;
  onChangeField: (field: IField) => void;
  onChangeOrder: (order: SortOrder) => void;
  onClick?: () => void;
  order: SortOrder;
  size?: SizeType;
}

const ActivitySort: React.FC<Props> = ({
  activityStatusOption,
  field,
  onApply,
  onChangeField,
  onChangeOrder,
  onClick,
  order,
  size = "large",
}) => {
  const intl = useIntl();
  const [visible, setVisibility] = useState(false);

  const fieldLabel =
    field.name === ActivityFieldName.START_AT
      ? activityStatusOption === ActivityStatusOption.ALL ||
        activityStatusOption === ActivityStatusOption.COMPLETED
        ? "dueDateOverride"
        : activityStatusOption === ActivityStatusOption.OVERDUE
        ? "dueDate"
        : ActivityFieldName.START_AT
      : field.name;

  const quickSortStatus =
    fieldLabel === "dueDateOverride"
      ? activityStatusOption === ActivityStatusOption.ALL
        ? "dueDate"
        : ActivityFieldName.START_AT
      : fieldLabel;

  const handleApply = useCallback(() => {
    onApply();
    setVisibility(false);
  }, [onApply]);

  const handleSelectField = useCallback(
    (name: string) => {
      const field = activityFieldModel.getByName(name);
      if (field) {
        onChangeField(field);
      }
    },
    [onChangeField]
  );

  useEffect(() => {
    if (activityStatusOption === ActivityStatusOption.OVERDUE) {
      if (field.name === ActivityFieldName.COMPLETED_AT) {
        const newField = activityFieldModel.getByName("startAt");
        const newOrder = SortOrder.DESC;
        if (newField) {
          onChangeField(newField);
          onChangeOrder(newOrder);
        }
      }
    } else if (activityStatusOption === ActivityStatusOption.UPCOMING) {
      if (field.name === ActivityFieldName.COMPLETED_AT) {
        const newField = activityFieldModel.getByName("startAt");
        const newOrder = SortOrder.DESC;
        if (newField) {
          onChangeField(newField);
          onChangeOrder(newOrder);
        }
      }
    } else if (activityStatusOption === ActivityStatusOption.NOT_SCHEDULED) {
      if (
        field.name === ActivityFieldName.COMPLETED_AT ||
        field.name === ActivityFieldName.START_AT
      ) {
        const newField = activityFieldModel.getByName("updatedAt");
        const newOrder = SortOrder.DESC;
        if (newField) {
          onChangeField(newField);
          onChangeOrder(newOrder);
        }
      }
    }
  }, [field, activityStatusOption, onChangeOrder, onChangeField]);

  const sortOptions = useSortOptions(intl, activityStatusOption);

  const handleSortingCancel = useCallback(() => {
    setVisibility(false);
  }, [setVisibility]);

  return (
    <Dropdown
      onVisibleChange={setVisibility}
      overlay={
        <div className={styles.container}>
          <Row gutter={[layout.spacerS, layout.spacerM]}>
            <Col span={24}>
              <SelectField<string>
                className={styles.select}
                label={intl.formatMessage({
                  id: "activityList.sort.fieldSelect",
                  defaultMessage: "Sort criteria",
                  description: "Activities list sort field select label",
                })}
                onChange={handleSelectField}
                options={sortOptions}
                size={size}
                value={field.name}
              />
            </Col>
            <Col span={24}>
              <SelectField<SortOrder>
                className={styles.select}
                label={intl.formatMessage({
                  id: "activityList.sort.orderSelect",
                  defaultMessage: "Sort direction",
                  description: "Activities list sort direction select label",
                })}
                onChange={onChangeOrder}
                size={size}
                value={order}
              >
                <Select.Option value={SortOrder.DESC}>
                  {getSortDirectionNameForField(intl, fieldLabel, SortOrder.DESC)}
                </Select.Option>
                <Select.Option value={SortOrder.ASC}>
                  {getSortDirectionNameForField(intl, fieldLabel, SortOrder.ASC)}
                </Select.Option>
              </SelectField>
            </Col>
            <Col span={24}>
              <span className={styles.disclaimer}>
                {intl.formatMessage({
                  id: "activityList.disclaimer.sort",
                  defaultMessage:
                    "Note that sort options may differ based on selected status filter.",
                  description: "Disclaimer to sort modal",
                })}
              </span>
            </Col>
            <Col span={24}>
              <Row gutter={layout.spacerS} justify="end">
                <Col>
                  <Button onClick={handleSortingCancel}>
                    {intl.formatMessage({
                      id: "activityList.sort.cancel",
                      defaultMessage: "Cancel",
                      description: "Activities list sort select cancel",
                    })}
                  </Button>
                </Col>
                <Col>
                  <Button onClick={handleApply} type="primary">
                    {intl.formatMessage({
                      id: "activityList.sort.apply",
                      defaultMessage: "Apply",
                      description: "Activities list sort select apply",
                    })}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      }
      trigger={["click"]}
      visible={visible}
    >
      <Button
        leftIcon={
          <FontAwesomeIcon icon={order === SortOrder.DESC ? faSortAmountDown : faSortAmountUp} />
        }
        onClick={onClick}
        size={size}
      >
        {intl.formatMessage(
          sortOptionsMessages[quickSortStatus as keyof typeof sortOptionsMessages]
        )}
      </Button>
    </Dropdown>
  );
};

export default ActivitySort;
